import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  Button,
  Input,
  Select,
  Typography,
  Space,
  Steps,
  Alert,
  message,
  Spin,
  Modal,
  Collapse,
} from "antd";
import {
  ArrowLeftOutlined,
  ThunderboltOutlined,
  SaveOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { api } from "@/services/api";
import { API_ENDPOINTS } from "@/constants";
import { CardPreviewTable } from "@/components/import/CardPreviewTable";
import type { PreviewCard } from "@/components/import/CardPreviewTable";
import type { ApiResponse, Subject, Chapter } from "@/types";

const { Title, Text } = Typography;
const { TextArea } = Input;

interface ParseResponse {
  cards: PreviewCard[];
}
interface PreviewResponse {
  previewToken: string;
  warnings: string[];
  duplicateCards: number[];
}
interface CommitResponse {
  deckId: string;
  cardCount: number;
}

export default function ImportPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(0);
  const [rawText, setRawText] = useState("");
  const [topicName, setTopicName] = useState("");
  const [selectedBoardId, setSelectedBoardId] = useState<string>();
  const [selectedSubjectId, setSelectedSubjectId] = useState<string>();
  const [selectedChapterId, setSelectedChapterId] = useState<string>();
  const [cards, setCards] = useState<PreviewCard[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [previewToken, setPreviewToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [newChapterModal, setNewChapterModal] = useState(false);
  const [newChapterName, setNewChapterName] = useState("");
  const [creatingChapter, setCreatingChapter] = useState(false);
  const [moreText, setMoreText] = useState("");
  const [parsingMore, setParsingMore] = useState(false);

  const { data: limitsResponse } = useQuery({
    queryKey: ["config", "limits"],
    queryFn: () =>
      api.get<never, ApiResponse<{ maxCardsPerDeck: number }>>(
        "/admin/config/limits",
      ),
    staleTime: 5 * 60 * 1000,
  });
  const maxCards: number = limitsResponse?.data?.maxCardsPerDeck ?? 10;

  const { data: meData } = useQuery({
    queryKey: ["users", "me"],
    queryFn: () => api.get<never, ApiResponse<{ subjectIds: string[] }>>(API_ENDPOINTS.USERS_ME),
    staleTime: 5 * 60 * 1000,
  });
  const teacherSubjectIds: string[] = meData?.data?.subjectIds ?? [];

  const { data: boardsData } = useQuery({
    queryKey: ["curriculum", "boards"],
    queryFn: () =>
      api.get<never, ApiResponse<any[]>>(API_ENDPOINTS.CURRICULUM_BOARDS),
  });

  const { data: chaptersData, refetch: refetchChapters } = useQuery({
    queryKey: ["curriculum", "chapters", selectedSubjectId],
    queryFn: () =>
      api.get<never, ApiResponse<Chapter[]>>(
        `${API_ENDPOINTS.CURRICULUM_CHAPTERS}?subjectId=${selectedSubjectId}`,
      ),
    enabled: Boolean(selectedSubjectId),
  });

  const allBoards: { id: string; name: string; subjects: Subject[] }[] =
    boardsData?.data ?? [];
  const boards = useMemo(() =>
    teacherSubjectIds.length > 0
      ? allBoards
          .map((b) => ({ ...b, subjects: b.subjects.filter((s) => teacherSubjectIds.includes(s.id)) }))
          .filter((b) => b.subjects.length > 0)
      : allBoards,
    [allBoards, teacherSubjectIds],
  );
  const subjects: Subject[] =
    boards.find((b) => b.id === selectedBoardId)?.subjects ?? [];
  const chapters: Chapter[] = chaptersData?.data ?? [];

  const handleParse = async () => {
    if (!rawText.trim()) {
      message.error("Please paste some content first");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post<never, ApiResponse<ParseResponse>>(
        API_ENDPOINTS.IMPORT_PARSE,
        { rawText },
      );
      const parsed = res.data.cards.slice(0, maxCards);
      if (res.data.cards.length > maxCards) {
        message.warning(
          `Only the first ${maxCards} cards were kept (max per deck)`,
        );
      }
      setCards(parsed);
      setStep(1);
    } catch (e: any) {
      message.error(e?.response?.data?.error?.message ?? "Parse failed");
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!topicName.trim()) {
      message.error("Topic name is required");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post<never, ApiResponse<PreviewResponse>>(
        API_ENDPOINTS.IMPORT_PREVIEW,
        {
          topicName,
          subjectId: selectedSubjectId,
          chapterId: selectedChapterId,
          cards,
        },
      );
      setWarnings(res.data.warnings);
      setPreviewToken(res.data.previewToken);
      const hasDeckDupe = res.data.warnings.some((w) =>
        w.startsWith("DUPLICATE_DECK"),
      );
      if (hasDeckDupe) {
        message.error("Deck already exists — rename the topic name");
      } else {
        setStep(2);
      }
    } catch (e: any) {
      message.error(e?.response?.data?.error?.message ?? "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  const handleCommit = async () => {
    setLoading(true);
    try {
      const res = await api.post<never, ApiResponse<CommitResponse>>(
        API_ENDPOINTS.IMPORT_COMMIT,
        { previewToken },
      );
      message.success(`Deck created with ${res.data.cardCount} cards!`);
      navigate("/teacher/decks");
    } catch (e: any) {
      message.error(e?.error?.message ?? "Commit failed");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateChapter = async () => {
    if (!newChapterName.trim() || !selectedSubjectId) return;
    setCreatingChapter(true);
    try {
      const res = await api.post<never, ApiResponse<Chapter>>(
        API_ENDPOINTS.CURRICULUM_CHAPTERS,
        {
          subjectId: selectedSubjectId,
          name: newChapterName.trim(),
        },
      );
      await refetchChapters();
      queryClient.invalidateQueries({
        queryKey: ["curriculum", "chapters", selectedSubjectId],
      });
      setSelectedChapterId(res.data.id);
      setNewChapterModal(false);
      setNewChapterName("");
      message.success(`Chapter "${res.data.name}" created`);
    } catch (e: any) {
      message.error(
        e?.response?.data?.error?.message ?? "Failed to create chapter",
      );
    } finally {
      setCreatingChapter(false);
    }
  };

  const handleAddBlankCard = () => {
    if (cards.length >= maxCards) {
      message.warning(`Maximum ${maxCards} cards allowed per deck`);
      return;
    }
    setCards((prev) => [
      ...prev,
      { title: "", frontText: "", backText: undefined },
    ]);
  };

  const handleParseMore = async () => {
    if (!moreText.trim()) return;
    setParsingMore(true);
    try {
      const res = await api.post<never, ApiResponse<ParseResponse>>(
        API_ENDPOINTS.IMPORT_PARSE,
        { rawText: moreText },
      );
      const available = maxCards - cards.length;
      if (available <= 0) {
        message.warning(`Maximum ${maxCards} cards already reached`);
        return;
      }
      const toAdd = res.data.cards.slice(0, available);
      if (res.data.cards.length > available) {
        message.warning(
          `Only ${toAdd.length} of ${res.data.cards.length} cards added (deck limit: ${maxCards})`,
        );
      }
      setCards((prev) => [...prev, ...toAdd]);
      setMoreText("");
      message.success(
        `Added ${toAdd.length} more card${toAdd.length === 1 ? "" : "s"}`,
      );
    } catch (e: any) {
      message.error(e?.response?.data?.error?.message ?? "Parse failed");
    } finally {
      setParsingMore(false);
    }
  };

  // Auto-select board when teacher only teaches one
  useEffect(() => {
    if (boards.length === 1 && !selectedBoardId) {
      setSelectedBoardId(boards[0].id);
    }
  }, [boards, selectedBoardId]);

  const chapterOptions = [
    ...chapters.map((c) => ({ value: c.id, label: c.name })),
  ];

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "24px 16px" }}>
      <Space style={{ marginBottom: 16 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/teacher/decks")}
        >
          Back to Decks
        </Button>
      </Space>
      <Title level={3}>Import Cards from ChatGPT</Title>

      <Steps
        current={step}
        style={{ marginBottom: 24 }}
        items={[
          { title: "Paste Content" },
          { title: "Review Cards" },
          { title: "Save Deck" },
        ]}
      />

      <Spin spinning={loading}>
        {step === 0 && (
          <Card>
            <Space direction="vertical" style={{ width: "100%" }} size="large">
              <div>
                <Text strong>Step 1: Paste your ChatGPT content</Text>
                <TextArea
                  rows={14}
                  value={rawText}
                  onChange={(e) => setRawText(e.target.value)}
                  placeholder="Paste the full ChatGPT conversation here (numbered sections become individual cards)..."
                  style={{ marginTop: 8 }}
                  maxLength={20000}
                  showCount
                />
              </div>
              <Button
                type="primary"
                icon={<ThunderboltOutlined />}
                onClick={handleParse}
                disabled={!rawText.trim()}
              >
                Parse into Cards
              </Button>
            </Space>
          </Card>
        )}

        {step === 1 && (
          <Card>
            <Space direction="vertical" style={{ width: "100%" }} size="large">
              <div>
                <Text strong>
                  Step 2: Review and edit cards, then set deck details
                </Text>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr 1fr",
                    gap: 12,
                    marginTop: 12,
                  }}
                >
                  <div>
                    <Text style={{ display: "block", marginBottom: 4 }}>
                      Topic Name *
                    </Text>
                    <Input
                      value={topicName}
                      onChange={(e) => setTopicName(e.target.value)}
                      placeholder="e.g. Pythagoras Theorem"
                      maxLength={100}
                      style={{ height: 32 }}
                    />
                  </div>
                  <div>
                    <Text style={{ display: "block", marginBottom: 4 }}>
                      Board
                    </Text>
                    <Select
                      style={{ width: "100%" }}
                      placeholder="Select board"
                      value={selectedBoardId}
                      onChange={(v) => {
                        setSelectedBoardId(v);
                        setSelectedSubjectId(undefined);
                        setSelectedChapterId(undefined);
                      }}
                      options={boards.map((b) => ({
                        value: b.id,
                        label: b.name,
                      }))}
                      allowClear
                    />
                  </div>
                  <div>
                    <Text style={{ display: "block", marginBottom: 4 }}>
                      Subject
                    </Text>
                    <Select
                      style={{ width: "100%" }}
                      placeholder={
                        selectedBoardId
                          ? "Select subject"
                          : "Select a board first"
                      }
                      disabled={!selectedBoardId}
                      value={selectedSubjectId}
                      onChange={(v) => {
                        setSelectedSubjectId(v);
                        setSelectedChapterId(undefined);
                      }}
                      options={subjects.map((s) => ({
                        value: s.id,
                        label: s.name,
                      }))}
                      allowClear
                    />
                  </div>
                  <div>
                    <Text style={{ display: "block", marginBottom: 4 }}>
                      Chapter
                    </Text>
                    <div style={{ display: "flex", gap: 6 }}>
                      <Select
                        style={{ flex: 1 }}
                        placeholder={
                          selectedSubjectId
                            ? chapters.length === 0
                              ? "No chapters — create one"
                              : "Select chapter"
                            : "Select subject first"
                        }
                        value={selectedChapterId}
                        onChange={setSelectedChapterId}
                        options={chapterOptions}
                        disabled={!selectedSubjectId}
                        allowClear
                        notFoundContent={
                          <div style={{ padding: 8, textAlign: "center" }}>
                            <Text type="secondary">No chapters found</Text>
                          </div>
                        }
                      />
                      <div
                        onClick={() =>
                          selectedSubjectId && setNewChapterModal(true)
                        }
                        title="Create new chapter"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 32,
                          alignSelf: "stretch",
                          border: "1px solid #d9d9d9",
                          borderRadius: 6,
                          cursor: selectedSubjectId ? "pointer" : "not-allowed",
                          opacity: selectedSubjectId ? 1 : 0.4,
                          color: "#595959",
                          flexShrink: 0,
                        }}
                      >
                        <PlusOutlined />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {warnings
                .filter((w) => w.startsWith("DUPLICATE_DECK"))
                .map((w, i) => (
                  <Alert
                    key={i}
                    type="error"
                    message={w.replace("DUPLICATE_DECK: ", "")}
                  />
                ))}
              {cards.length > 0 && cards.length < 4 && (
                <Alert
                  type="warning"
                  message={
                    <span>
                      Only <strong>{cards.length}</strong> card
                      {cards.length === 1 ? "" : "s"} parsed — a minimum of{" "}
                      <strong>4 cards</strong> is required. Paste more content
                      or add cards manually below.
                    </span>
                  }
                />
              )}
              <CardPreviewTable
                cards={cards}
                onChange={setCards}
                warnings={warnings}
              />

              {cards.length >= maxCards && (
                <Alert
                  type="warning"
                  message={`Maximum ${maxCards} cards reached. Remove a card to add more.`}
                  showIcon
                />
              )}

              <Space wrap>
                <Button
                  icon={<PlusOutlined />}
                  onClick={handleAddBlankCard}
                  disabled={cards.length >= maxCards}
                >
                  Add Card Manually
                </Button>
              </Space>

              <Collapse
                size="small"
                items={[
                  {
                    key: "more",
                    label: "Paste more content to append cards",
                    children: (
                      <Space direction="vertical" style={{ width: "100%" }}>
                        <Input.TextArea
                          rows={6}
                          value={moreText}
                          onChange={(e) => setMoreText(e.target.value)}
                          placeholder="Paste additional ChatGPT content here — parsed cards will be appended to the list above"
                          maxLength={20000}
                          showCount
                        />
                        <Button
                          type="default"
                          icon={<ThunderboltOutlined />}
                          onClick={handleParseMore}
                          loading={parsingMore}
                          disabled={!moreText.trim()}
                        >
                          Parse & Append
                        </Button>
                      </Space>
                    ),
                  },
                ]}
              />

              <Space>
                <Button onClick={() => setStep(0)}>Back</Button>
                <Button
                  type="primary"
                  onClick={handlePreview}
                  disabled={cards.length === 0}
                >
                  Validate & Continue ({cards.length}{" "}
                  {cards.length === 1 ? "card" : "cards"})
                </Button>
              </Space>
            </Space>
          </Card>
        )}

        {step === 2 && (
          <Card>
            <Space direction="vertical" style={{ width: "100%" }} size="large">
              <Alert
                type="success"
                message={`Ready to save: "${topicName}" with ${cards.length} cards as DRAFT`}
                description="The deck will be saved as a draft. You can review and publish it from the Decks page."
              />
              {warnings.filter((w) => !w.startsWith("DUPLICATE_DECK")).length >
                0 && (
                <Alert
                  type="warning"
                  message="Warnings"
                  description={warnings
                    .filter((w) => !w.startsWith("DUPLICATE_DECK"))
                    .join("; ")}
                />
              )}
              <Space>
                <Button onClick={() => setStep(1)}>Back to Edit</Button>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleCommit}
                >
                  Save as Draft
                </Button>
              </Space>
            </Space>
          </Card>
        )}
      </Spin>

      <Modal
        title="Create New Chapter"
        open={newChapterModal}
        onOk={handleCreateChapter}
        onCancel={() => {
          setNewChapterModal(false);
          setNewChapterName("");
        }}
        okText="Create"
        confirmLoading={creatingChapter}
        okButtonProps={{ disabled: !newChapterName.trim() }}
      >
        <Input
          placeholder="Chapter name (e.g. Lines and Angles)"
          value={newChapterName}
          onChange={(e) => setNewChapterName(e.target.value)}
          onPressEnter={handleCreateChapter}
          maxLength={100}
          autoFocus
        />
      </Modal>
    </div>
  );
}
